import React from 'react';
import { Mention, MentionsInput, SuggestionDataItem } from 'react-mentions';

interface UserMentionInputProps {
  disabled: boolean;
  height?: number;
  value: string;
  users?: Array<SuggestionDataItem>;
  placeholder?: string;
  onChange?: (event: any, value: string) => void;
}

export const UserMentionInput = (props: UserMentionInputProps) => {
  const { disabled, height, users, value, placeholder, onChange } = props;
  const userMentionInputRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    if (userMentionInputRef) {
      userMentionInputRef.current?.setAttribute('spellcheck', 'false');
    }
  }, [userMentionInputRef]); //eslint-disable-line

  return (
    <MentionsInput
      value={value}
      onChange={onChange ? onChange : () => {}}
      placeholder={
        placeholder ? placeholder : !disabled ? 'Type a comment' : value
      }
      a11ySuggestionsListLabel={'User List'}
      allowSuggestionsAboveCursor={true}
      style={
        disabled
          ? disabledMentionsInputStyle
          : getDefaultMentionsInputStyle({ height })
      }
      inputRef={userMentionInputRef}
    >
      <Mention
        markup="@[__display__](user:__id__)"
        displayTransform={(id: any, display: any) => `@${display}`}
        trigger="@"
        data={users || []}
        renderSuggestion={(
          suggestion: any,
          search: any,
          highlightedDisplay: any
        ) => <div className="user">{highlightedDisplay}</div>}
        onAdd={() => {}}
        style={disabled ? disabledMentionStyle : defaultMentionStyle}
        appendSpaceOnAdd={true}
      />
    </MentionsInput>
  );
};

const getDefaultMentionsInputStyle = (payload?: {
  height?: number;
  listMaxHeight?: number;
}) => ({
  control: {
    backgroundColor: '#fff',
    fontSize: 16 * 0.8,
    fontWeight: 'normal',
  },

  '&multiLine': {
    control: {
      fontFamily: 'Poppins',
      minHeight: payload?.height ? payload?.height : 51,
    },
    highlighter: {
      paddingLeft: 14,
      paddingRight: 14,
      paddingTop: 15,
      paddingBottom: 15,
      border: '1px solid transparent',
      boxSizing: 'border-box',
      overflow: 'hidden',
      maxHeight: 128,
    },
    input: {
      paddingLeft: 14,
      paddingRight: 14,
      paddingTop: 15,
      paddingBottom: 15,
      border: '1px solid silver',
      borderRadius: 4,
      overflow: 'auto',
      maxHeight: 128,
      outline: 'none',
    },
  },

  '&singleLine': {
    display: 'inline-block',
    width: 180,

    highlighter: {
      padding: 1,
      border: '2px inset transparent',
    },
    input: {
      padding: 1,
      border: '2px inset',
    },
  },

  suggestions: {
    list: {
      opacity: 1,
      transform: 'none',
      transformOrigin: 'center top',
      transition:
        'opacity 232ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 155ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      boxShadow:
        '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 14%), 0px 1px 8px 0px rgb(0 0 0 / 12%)',
      borderRadius: 4,
      overflow: 'auto',
      paddingTop: 8,
      paddingBottom: 8,
      maxHeight: 200,
    },
    item: {
      padding: '6px 16px',
      fontSize: 16 * 0.8,
      '&focused': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    },
  },
});

const defaultMentionStyle = {
  color: '#76B041',
  textShadow:
    '0.1px 0.1px 0.7px white, 0.1px -0.1px 0.7px white, -0.1px 0.1px 0.7px white, -0.1px -0.1px 0.7px white',
  zIndex: 1,
  position: 'relative',
};

// Disabled style: should be refactord by using 'react-mentions/lib/utils/merge', but this doesn't work for the moment.
const disabledMentionsInputStyle = {
  control: {
    backgroundColor: 'transparent',
    fontSize: 16 * 0.8,
    fontWeight: 'normal',
  },

  '&multiLine': {
    control: {
      fontFamily: 'Poppins',
      minHeight: 0,
    },
    highlighter: {
      padding: 0,
      border: 'none',
      boxSizing: 'border-box',
      overflow: 'hidden',
      maxHeight: 'unset',
    },
    input: {
      padding: 0,
      border: 'none',
      borderRadius: 4,
      overflow: 'auto',
      maxHeight: 'unset',
      outline: 'none',
      caretColor: 'transparent',
    },
  },

  '&singleLine': {
    display: 'inline-block',
    width: 180,

    highlighter: {
      padding: 1,
      border: '2px inset transparent',
    },
    input: {
      padding: 1,
      border: '2px inset',
    },
  },

  suggestions: {
    list: {
      opacity: 1,
      transform: 'none',
      transformOrigin: 'center top',
      transition:
        'opacity 232ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 155ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      boxShadow:
        '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 14%), 0px 1px 8px 0px rgb(0 0 0 / 12%)',
      borderRadius: 4,
      overflow: 'auto',
      paddingTop: 8,
      paddingBottom: 8,
      maxHeight: 200,
    },
    item: {
      padding: '6px 16px',
      fontSize: 16 * 0.8,
      '&focused': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    },
  },
};

const disabledMentionStyle = {
  color: '#76B041',
  textShadow:
    '0.1px 0.1px 0.7px white, 0.1px -0.1px 0.7px white, -0.1px 0.1px 0.7px white, -0.1px -0.1px 0.7px white',
  zIndex: 1,
  position: 'relative',
};
